// AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import { init, auth, client, subscription } from 'cubeforall';

// Create the context
const AuthContext = createContext();
// Session storage keys
const CCN_SESSION_KEY = 'ccn_session';
const CCN_USER_KEY = 'ccn_user';
const CCN_SUBSCRIPTION_KEY = 'ccn_subscription';
// Provider component
export const AuthProvider = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated: isAuth0Authenticated,
    logout,
    isLoading: isAuth0Loading,
    loginWithPopup,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();

  const [isAuthLoading, setAuthLoading] = useState(true);
  const [userCredit, setUserCredit] = useState(0);
  const [userCreditUsed, setUserCreditUsed] = useState(0);
  const [isFetchingCredit, setIsFetchingCredit] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  const [isSubUser, setIsSubUser] = useState(false);
  const [userRegistered, setUserRegistered] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [isCCN, setIsCCN] = useState(false);
  const [ccnUser, setCcnUser] = useState(null);
  const [ccnSubscription, setCcnSubscription] = useState(null);

  init('moaah', '70097f4a-f125-4231-b12e-03a3b6e11043', process.env.NODE_ENV === 'production' ? 'sandbox' : 'sandbox');

  const tier = {
    "1": "Starter",
    "2": "Premium",
    "10": "Premium",
    "free": "Free",
    "starter": "Starter",
    "premium": "Premium"
  }

  const color = {
    "1": "success",
    "2": "info",
    "10": "info",
    "free": "secondary",
    "starter": "success",
    "premium": "info"
  }

  const formatCcnUser = (account) => {
    if (!account?.idTokenClaims) {
      throw new Error('Invalid account object');
    }

    const idToken = account.idTokenClaims;

    let uifo = {};

    return {
      user_metadata: {
        bsi: false,
        ccn: true
      },
      given_name: idToken.name,
      family_name: "User",
      nickname: idToken.name,
      name: idToken.name,
      picture: "",
      updated_at: new Date().toISOString(),
      email: idToken.email,
      email_verified: true,
      sub: idToken.sub,
      ccnData: {
        contactNo: idToken.extension_ContactNo,
        cubeUserId: idToken.extension_CubeUserID,
        city: idToken.city,
        countryCode: idToken.extension_CountryCode,
        uifo: uifo
      }
    };
  };

  const formatCcnSubscription = async (account, subData, useDummyData = false) => {
    const creditsUsed = await checkCcnCredit(account.email);
    if (useDummyData) {
      const now = new Date();
      const periodEnd = new Date(now);
      periodEnd.setDate(now.getDate() + 30);
      return {
        data: {
          sub_id: `ccn-dummy-${Math.random().toString(36).substring(2, 8)}`,
          customer_id: 'ccn-customer',
          user_id: account.email,
          current_period_start: now.toISOString(),
          current_period_end: periodEnd.toISOString(),
          credits_purchased: "1000",
          invoice_id: `ccn-${Date.now()}`,
          updated: now.toISOString(),
          tier: 2,
          active: true,
          credits_used: creditsUsed,
          terminated: false,
          curr_price_id: "652d04d9512779db0acdce0c",
          next_price_id: "652d04d9512779db0acdce0c",
          live_mode: false,
          last_used: now.toISOString()
        },
        invoice: {
          total: 0
        },
        detail: {},
        tierName: "Premium",
        tierColor: "info"
      };
    }

    if (!subData) {
      throw new Error('subData is required when not using dummy data');
    }

    const priceData = subData.priceData || {};
    const tierId = priceData._id;
    const userPlanId = subData.userPlanId;

    const tierConfig = {
      "652d04d9512779db0acdce08": {
        key: 1,
        credits: 100,
        name: "Starter",
        color: "success"
      },
      "652d04d9512779db0acdce0c": {
        key: 2,
        credits: 1000,
        name: "Premium",
        color: "info"
      },
      default: {
        key: 0,
        credits: 0,
        name: "Free",
        color: "primary"
      }
    };

    const tier = tierId ? (tierConfig[tierId] || tierConfig.default) : tierConfig.default;
    const now = new Date();
    const periodEnd = new Date(now);
    periodEnd.setDate(now.getDate() + 30);

    const subscription = {
      data: {
        sub_id: `${userPlanId}`,
        customer_id: account.nickname,
        user_id: account.email,
        current_period_start: now.toISOString(),
        current_period_end: periodEnd.toISOString(),
        credits_purchased: tier.credits.toString(),
        invoice_id: `ccn-inv-${Date.now()}`,
        updated: now.toISOString(),
        tier: tier.key,
        active: subData.status !== false,
        credits_used: "0",
        terminated: false,
        curr_price_id: tierId,
        next_price_id: tierId,
        live_mode: true,
        last_used: now.toISOString()
      },
      invoice: {
        total: 0
      },
      detail: {},
      tierName: tier.name,
      tierColor: tier.color
    };

    return subscription;
  };

  const checkCcnCredit = async (email) => {
    setIsFetchingCredit(true);
    try {
      const response = await fetch(`https://mtech-api.com/web-app/ccn/credit?email=${ccnUser?.email || email}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': ccnUser?.email || email || "ccn-user@example.com",
          'x-user-sub': ccnSubscription?.data?.sub_id || "free"
        }
      });

      if (response.ok) {
        const data = await response.json();
        return parseInt(data.count, 10) || 0;
      }
      return 0;
    } catch (error) {
      console.error('Error checking CCN credit:', error);
      return 0;
    } finally {
      setIsFetchingCredit(false);
    }
  };

  const burnUserCredit = async () => {
    const maxRetries = 5;
    let retryCount = 0;

    if (isCCN) {
      const credits = await checkCcnCredit();
      if (ccnSubscription.data.credits_purchased - credits <= 0) {
        toast.error("No credits left. Please upgrade your plan.");
        return false;
      }
      setUserCredit(prevCredit => prevCredit - 1);
      setUserCreditUsed(credits);
      return true;
    } else {
      if (!isAuth0Authenticated || !userSubscription) {
        toast.error("Please sign in to start the free trial with search credits.");
        return false;
      } else if (!userSubscription) {
        toast.warn("Please wait for subscription details to be loaded");
        return false;
      }

      const payload = {
        subId: userSubscription?.data?.sub_id,
        customerId: userSubscription?.data?.customer_id,
        subUserId: isSubUser ? auth0User.email : null
      };

      while (retryCount < maxRetries) {
        try {
          const token = await getAccessTokenSilently();
          const response = await fetch(`https://mtech-api.com/users-smart-hs/subscription/useCredit`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });

          if (response.ok) {
            setUserCredit(prevCredit => prevCredit - 1);
            setUserCreditUsed(prevCreditUsed => prevCreditUsed + 1);
            return true;
          } else {
            const errorData = await response.text();
            if (errorData === "No trial credits left") {
              toast.error("No trial credits left. Please subscribe to continue.");
              return false;
            } else if (retryCount + 1 === maxRetries) {
              toast.error("You have no credits left.");
              return false;
            }
          }
        } catch (error) {
          console.error(`Attempt ${retryCount + 1} failed:`, error);
        }
        retryCount++;
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    return false;
  };

  useEffect(() => {
    const checkInitialCredit = async () => {
      if (!isCCN || !ccnUser) return;
      const credits = await checkCcnCredit();
      setUserCredit(ccnSubscription.data.credits_purchased - credits);
      setUserCreditUsed(credits);
    };

    checkInitialCredit();
  }, [isCCN, ccnSubscription]);

  const saveCCNSession = (user, subscription) => {
    sessionStorage.setItem(CCN_SESSION_KEY, 'active');
    sessionStorage.setItem(CCN_USER_KEY, JSON.stringify(user));
    sessionStorage.setItem(CCN_SUBSCRIPTION_KEY, JSON.stringify(subscription));
  };

  const clearCCNSession = () => {
    sessionStorage.removeItem(CCN_SESSION_KEY);
    sessionStorage.removeItem(CCN_USER_KEY);
    sessionStorage.removeItem(CCN_SUBSCRIPTION_KEY);
  };

  const getCCNSession = () => {
    const sessionActive = sessionStorage.getItem(CCN_SESSION_KEY);
    if (!sessionActive) return null;

    try {
      const user = JSON.parse(sessionStorage.getItem(CCN_USER_KEY));
      const subscription = JSON.parse(sessionStorage.getItem(CCN_SUBSCRIPTION_KEY));
      return { user, subscription };
    } catch (error) {
      console.error('Error parsing stored CCN session:', error);
      clearCCNSession();
      return null;
    }
  };

  const checkExistingCCNSession = async () => {
    try {
      setAuthLoading(true);

      const storedSession = getCCNSession();
      if (storedSession) {
        setCcnUser(storedSession.user);
        setCcnSubscription(storedSession.subscription);
        await saveCCNSubsInfo(storedSession.subscription);

        setIsCCN(true);
        console.log("Restored CCN session from storage");
        return;
      }

      const account = auth.getActiveAccount();
      if (!account) {
        console.log("No active CCN account found");
        return;
      }

      const isAuthenticated = await auth.isAuthenticated();
      if (!isAuthenticated) {
        console.log("CCN session expired");
        return;
      }

      const subResponse = await subscription.checkSubscription();
      const formattedUser = formatCcnUser(account, subResponse);
      const formattedSubscription = await formatCcnSubscription(formattedUser, subResponse);
      saveCCNSubsInfo(formattedSubscription);
      saveCCNSession(formattedUser, formattedSubscription);

      setCcnUser(formattedUser);
      setCcnSubscription(formattedSubscription);
      setIsCCN(true);

      console.log("Reconnected to existing CCN session:", formattedUser);
    } catch (error) {
      console.error("CCN session check failed:", error);
      logoutCCN();
    } finally {
      setAuthLoading(false);
    }
  };

  const saveCCNSubsInfo = async (subscription) => {
    try {
      const response = await fetch('https://mtech-api.com/web-app/ccn/subs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': ccnUser?.email || "ccn-user@example.com",
          'x-user-sub': ccnSubscription?.data?.sub_id || "free"
        },
        body: JSON.stringify(subscription)
      });

      if (!response.ok) {
        throw new Error(`Failed to save subscription: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Subscription saved:', result);
      return result;
    } catch (error) {
      console.error('Error saving subscription:', error);
    }
  };


  useEffect(() => {
    const initialize = async () => {
      try {
        if (!isAuth0Authenticated) {
          await checkExistingCCNSession();
        }
      } catch (error) {
        console.error("Initialization error:", error);
      }
    };

    initialize();
  }, []);

  const handleLoginCCN = async () => {
    try {
      setAuthLoading(true);
      await auth.signIn().catch((error) => {
        console.error(error);
      });

      const account = auth.getActiveAccount();
      if (!account) {
        console.log("No active account found");
        return;
      }

      const subResponse = await subscription.checkSubscription();
      const formattedUser = formatCcnUser(account, subResponse);
      const formattedSubscription = await formatCcnSubscription(formattedUser, subResponse);
      saveCCNSubsInfo(formattedSubscription);

      saveCCNSession(formattedUser, formattedSubscription);

      setCcnUser(formattedUser);
      setCcnSubscription(formattedSubscription);
      setIsCCN(true);

      console.log("CCN User:", formattedUser);
      console.log("CCN Subscription:", formattedSubscription);
    } catch (error) {
      toast.error("CCN Login failed", error);
    } finally {
      setAuthLoading(false);
    }
  };

  const logoutCCN = () => {
    clearCCNSession();
    setCcnUser(null);
    setCcnSubscription(null);
    setIsCCN(false);
    setUserCredit(0);
    setUserCreditUsed(0);
    auth.signOut().then(() => {
    });
  };

  // Fetch subscription details for Auth0 users
  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      if (isCCN) return;

      if (isAuth0Authenticated && auth0User) {
        try {
          setIsFetchingCredit(true);
          const token = await getAccessTokenSilently();
          const [response, userResponse, responseApi] = await Promise.all([
            fetch(
              `https://mtech-api.com/users-smart-hs/subscription/current?userid=${auth0User?.email}`,
              {
                method: "GET",
                headers: {
                  "content-type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            ),
            fetch(
              `https://mtech-api.com/users-smart-hs?userid=${auth0User?.email}`,
              {
                method: "GET",
                headers: {
                  "content-type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            ),
            fetch(
              `https://mtech-api.com/users-smart-hs/subscription-api/current?userid=${auth0User?.email}`,
              {
                method: "GET",
                headers: {
                  "content-type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            ),
          ]);

          if (userResponse.ok) {
            const userData = await userResponse.json();
            setUserInfo(userData);
          }

          let subscriptionData = null;
          let userCredit = 0;
          let userCreditUsed = 0;

          if (response.ok && responseApi.ok) {
            const data = await response.json();
            const apiData = await responseApi.json();
            userCredit = data.data.credits_purchased - data.data.credits_used;
            subscriptionData = {
              ...data,
              detail: userInfo,
              tierName: tier[data.data.tier],
              tierColor: color[data.data.tier],
              api: { ...apiData, tierName: tier[apiData.data.tier], tierColor: color[apiData.data.tier] },
            };
            userCreditUsed = parseInt(subscriptionData?.data?.credits_used);
          }
          else if (response.ok) {
            const data = await response.json();
            userCredit = data.data.credits_purchased - data.data.credits_used;
            subscriptionData = {
              ...data,
              detail: userInfo,
              tierName: tier[data.data.tier],
              tierColor: color[data.data.tier],
            };
            userCreditUsed = parseInt(subscriptionData?.data?.credits_used);
          }
          else if (responseApi.ok) {
            const apiData = await responseApi.json();
            subscriptionData = {
              detail: userInfo,
              tierName: tier[apiData.data.tier],
              tierColor: color[apiData.data.tier],
              api: { ...apiData, tierName: tier[apiData.data.tier], tierColor: color[apiData.data.tier] },
            };
          }

          if (!response.ok) {
            const [checkSubResponse] = await Promise.all([
              fetch(
                `https://mtech-api.com/users-smart-hs/subscription/check_sub_user?user_email=${auth0User?.email}`,
                {
                  method: "GET",
                  headers: {
                    "content-type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                }
              ),
            ]);

            if (checkSubResponse.ok) {
              const checkSubData = await checkSubResponse.json();

              if (checkSubData?.data?.main_sub_id) {
                const [mainUserResponse] = await Promise.all([
                  fetch(
                    `https://mtech-api.com/users-smart-hs/subscription/main_user_credits?sub_id=${checkSubData.data.main_sub_id}`,
                    {
                      method: "GET",
                      headers: {
                        "content-type": "application/json",
                        Authorization: `Bearer ${token}`,
                      },
                    }
                  ),
                ]);

                if (mainUserResponse.ok) {
                  const mainUserData = await mainUserResponse.json();
                  subscriptionData = {
                    ...mainUserData,
                    tierName: tier[mainUserData.data.tier],
                    tierColor: color[mainUserData.data.tier],
                  };
                  userCredit = mainUserData.data.credits_purchased - mainUserData.data.credits_used;
                  userCreditUsed = parseInt(checkSubData.data.credits_used, 10);
                  setIsSubUser(true);
                }
              }
            }
          }

          setUserSubscription(subscriptionData);
          setUserCredit(userCredit);
          setUserCreditUsed(userCreditUsed || 0);

          if (!userResponse.ok) {
            const [userRegisterResponse] = await Promise.all([
              fetch(
                `https://mtech-api.com/users-smart-hs/stripe/customer/create?email=${auth0User?.email}&name=${auth0User?.name}`,
                {
                  method: "GET",
                  headers: {
                    "content-type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                }
              ),
            ]);
            if (userRegisterResponse.ok) {
              setUserRegistered(prev => !prev);
              setUserInfo(await userRegisterResponse.json());
            }
          }
        } catch (error) {
          setUserCredit(0);
        } finally {
          setIsFetchingCredit(false);
        }
      }
    };

    fetchSubscriptionDetails();
  }, [isAuth0Authenticated, auth0User, getAccessTokenSilently, userRegistered, isCCN]);

  useEffect(() => {
    if (!isAuth0Loading || isCCN) {
      setAuthLoading(false);
    }
  }, [isAuth0Loading, isCCN]);

  const handleLogin = async () => {
    try {
      setAuthLoading(true);
      await loginWithPopup();
      setIsCCN(false);
    } catch (error) {
      toast.error("Login failed", error);
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: isCCN ? ccnUser : auth0User,
        isAuthenticated: isCCN ? true : isAuth0Authenticated,
        isLoading: isAuthLoading,
        logout,
        logoutCCN,
        isAuthLoading,
        userCredit,
        userCreditUsed,
        handleLogin,
        handleLoginCCN,
        isFetchingCredit,
        userSubscription: isCCN ? ccnSubscription : userSubscription,
        getAccessTokenSilently,
        burnUserCredit,
        isSubUser,
        userInfo,
        loginWithRedirect,
        isCCN,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for easy access to AuthContext
export const useAuth = () => useContext(AuthContext);